import { forwardRef } from 'react';
import { Divider } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomList } from 'src/components/CustomList';
import { ToneOfVoiceList } from 'src/components/ToneOfVoiceList';
import { GENERATION_IMPROVE_MODE } from 'src/data/generation';

import { ChangeTovListItem } from './components/ChangeTovListItem';
import { HeaderListItem } from './components/HeaderListItem';
import { InstructionsInput } from './components/InstructionsInput';
import { InternalListItem } from './components/InternalListItem';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  onSettingsClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  ToneOfVoiceListProps: PropTypes.shape(ToneOfVoiceList.propTypes).isRequired,
  isBoostScoreAllowed: PropTypes.bool,
  displayHeader: PropTypes.bool,
  disabled: PropTypes.bool,
};
const defaultProps = {
  className: '',
  onSettingsClick: null,
  isBoostScoreAllowed: false,
  displayHeader: false,
  disabled: false,
};

export const GenerationImproveList = forwardRef(
  (
    {
      className,
      onSettingsClick,
      onSubmit,
      ToneOfVoiceListProps,
      isBoostScoreAllowed,
      displayHeader,
      disabled,
    },
    ref
  ) => {
    const styles = useStyles();

    const handleListClick = (event) => {
      event.stopPropagation(); // Prevents the menu from closing when clicking on the list
    };

    const handleListItemClick = (improveMode) => {
      onSubmit({ improveMode });
    };

    const handleTovSelect = (result) => {
      onSubmit({ improveMode: GENERATION_IMPROVE_MODE.changeTov, tov: result });
    };

    const handleInstructionsSubmit = (instructions) => {
      onSubmit({ improveMode: GENERATION_IMPROVE_MODE.instructions, instructions });
    };

    const renderListItem = (mode) => (
      <InternalListItem
        className={styles.internalListItem}
        key={mode}
        mode={mode}
        onClick={handleListItemClick}
        disabled={disabled}
      />
    );

    return (
      <CustomList
        className={cx(styles.root, className)}
        ref={ref}
        onClick={handleListClick}
        data-testid="GenerationImproveList"
      >
        {displayHeader && (
          <>
            <HeaderListItem
              onSettingsClick={onSettingsClick}
              disabled={disabled}
            />

            <Divider className={styles.divider} />
          </>
        )}

        {isBoostScoreAllowed
          ? [
              GENERATION_IMPROVE_MODE.boostScoreEngaging,
              GENERATION_IMPROVE_MODE.boostScoreSimilar,
            ].map(renderListItem)
          : [GENERATION_IMPROVE_MODE.improveSimilar].map(renderListItem)}

        <Divider className={styles.divider} />

        {[
          GENERATION_IMPROVE_MODE.makeCasual,
          GENERATION_IMPROVE_MODE.makeLonger,
          GENERATION_IMPROVE_MODE.makeShorter,
        ].map(renderListItem)}

        <ChangeTovListItem
          className={styles.changeTovListItem}
          ToneOfVoiceListProps={{
            ...ToneOfVoiceListProps,
            onSelect: handleTovSelect,
          }}
          disabled={disabled}
        />

        <Divider className={styles.divider} />

        <div className={styles.instructionsInputWrapper}>
          <InstructionsInput
            className={styles.instructionsInput}
            onSubmit={handleInstructionsSubmit}
            disabled={disabled}
          />
        </div>
      </CustomList>
    );
  }
);

GenerationImproveList.displayName = 'GenerationImproveList';
GenerationImproveList.propTypes = propTypes;
GenerationImproveList.defaultProps = defaultProps;
